import { useState, useEffect, useRef, useCallback } from 'react';
import { portalService } from '../../services/portal';
import { adminService } from '../../services/admin';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import styles from './SandboxToggle.module.css';

/**
 * Sandbox mode indicator/toggle in the portal navbar.
 *
 * When sandbox mode is active, shows an orange "Sandbox" indicator with
 * a configure dropdown for the redirect email.  Emails in sandbox mode
 * are redirected to the configured address with a test banner.
 *
 * In staging (or local), the toggle is functional and calls the admin
 * API to enable/disable sandbox mode at runtime.  In production the
 * toggle shows a "not available" toast.
 */
const SandboxToggle: React.FC = () => {
  const [sandboxMode, setSandboxMode] = useState<boolean | null>(null);
  const [canToggle, setCanToggle] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Redirect email state
  const [redirectEmail, setRedirectEmail] = useState('');
  const [redirectInput, setRedirectInput] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [savingRedirect, setSavingRedirect] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const configBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    portalService.getPortalConfig().then(config => {
      setSandboxMode(config.sandbox_mode);
      setCanToggle(config.sandbox_toggleable);
      setRedirectEmail(config.sandbox_redirect_email || '');
      setRedirectInput(config.sandbox_redirect_email || '');
    }).catch(() => {
      setSandboxMode(true);
    });
  }, []);

  const closeDropdown = useCallback(() => setShowDropdown(false), []);
  useClickOutside(dropdownRef, closeDropdown, showDropdown);
  // Escape closes the redirect popup and returns focus to the gear that
  // opened it (proposal 9: a keyboard-only tester could not close it).
  const escapeDropdown = useCallback(() => {
    setShowDropdown(false);
    configBtnRef.current?.focus();
  }, []);
  useEscapeKey(escapeDropdown, showDropdown);

  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const handleToggleClick = async () => {
    if (!canToggle) {
      showToast('Sandbox mode can only be toggled in staging');
      return;
    }

    if (toggling) return;

    const newValue = !sandboxMode;
    setToggling(true);
    try {
      const result = await adminService.toggleSandboxMode(newValue);
      setSandboxMode(result.sandbox_mode);
      showToast(
        result.sandbox_mode
          ? 'Sandbox mode enabled — emails will be redirected'
          : 'Sandbox mode disabled — live email sending active'
      );
    } catch {
      showToast('Failed to toggle sandbox mode (admin access required)');
    } finally {
      setToggling(false);
    }
  };

  const handleSaveRedirect = async () => {
    if (savingRedirect) return;
    setSavingRedirect(true);
    try {
      const result = await adminService.setSandboxRedirect(redirectInput.trim());
      setRedirectEmail(result.sandbox_redirect_email);
      setRedirectInput(result.sandbox_redirect_email);
      setShowDropdown(false);
      showToast(
        result.sandbox_redirect_email
          ? `Emails will be redirected to ${result.sandbox_redirect_email}`
          : 'Redirect cleared — emails will be logged only'
      );
    } catch {
      showToast('Failed to update redirect email');
    } finally {
      setSavingRedirect(false);
    }
  };

  const handleClearRedirect = async () => {
    setRedirectInput('');
    setSavingRedirect(true);
    try {
      const result = await adminService.setSandboxRedirect('');
      setRedirectEmail(result.sandbox_redirect_email);
      setRedirectInput(result.sandbox_redirect_email);
      setShowDropdown(false);
      showToast('Redirect cleared — emails will be logged only');
    } catch {
      showToast('Failed to clear redirect email');
    } finally {
      setSavingRedirect(false);
    }
  };

  // Don't render until we know the state
  if (sandboxMode === null) return null;

  // In production, don't render the toggle at all
  if (!canToggle) return null;

  const isActive = sandboxMode;

  return (
    <>
      <div className={styles.wrapper} ref={dropdownRef}>
        <div className={`${styles.container} ${!isActive ? styles.containerOff : ''}`}>
          <span className={styles.icon}>{isActive ? '\u{1F9EA}' : '\u{1F7E2}'}</span>
          <span className={styles.label}>{isActive ? 'Sandbox' : 'Live'}</span>
          <button
            className={`${styles.toggle} ${!isActive ? styles.toggleOff : ''}`}
            onClick={handleToggleClick}
            type="button"
            disabled={toggling}
            aria-label={
              isActive
                ? 'Sandbox mode is active. Emails are redirected to a test address.'
                : 'Live mode is active. Emails will be sent.'
            }
            title={
              canToggle
                ? (isActive ? 'Click to enable live sending' : 'Click to enable sandbox')
                : 'Sandbox mode can only be toggled in staging'
            }
          />
          {isActive && (
            <button
              className={styles.configBtn}
              ref={configBtnRef}
              aria-expanded={showDropdown}
              onClick={() => setShowDropdown(!showDropdown)}
              type="button"
              title="Configure sandbox redirect"
              aria-label="Configure sandbox email redirect"
            >
              &#x2699;
            </button>
          )}
        </div>

        {/* Redirect info pill */}
        {isActive && redirectEmail && (
          <div
            className={styles.redirectPill}
            title={`Emails redirected to ${redirectEmail}`}
            onClick={() => setShowDropdown(!showDropdown)}
          >
            &#x2709; {redirectEmail}
          </div>
        )}

        {/* Info indicator when sandbox is active */}
        {isActive && !redirectEmail && (
          <div
            className={styles.infoPill}
            title="No redirect configured — emails are logged but not sent. Click to configure."
            onClick={() => setShowDropdown(!showDropdown)}
          >
            &#x2709; Not redirected
          </div>
        )}

        {/* Dropdown for configuring redirect */}
        {showDropdown && isActive && (
          <div className={styles.dropdown}>
            <div className={styles.dropdownHeader}>
              Redirect all emails to:
            </div>
            <div className={styles.dropdownHint}>
              Sandbox emails will be sent to this address with a banner
              showing the original recipients (To, CC, BCC).
            </div>
            <input
              className={styles.redirectInput}
              type="email"
              placeholder="test@example.com"
              value={redirectInput}
              onChange={e => setRedirectInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleSaveRedirect();
              }}
              autoFocus
            />
            <div className={styles.dropdownActions}>
              <button
                className={styles.saveBtn}
                onClick={handleSaveRedirect}
                disabled={savingRedirect}
                type="button"
              >
                {savingRedirect ? 'Saving...' : 'Save'}
              </button>
              {redirectEmail && (
                <button
                  className={styles.clearBtn}
                  onClick={handleClearRedirect}
                  disabled={savingRedirect}
                  type="button"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      {toastMessage && (
        <div className={styles.toast}>{toastMessage}</div>
      )}
    </>
  );
};

export default SandboxToggle;
